import { BaseButton } from "@ui/atoms/BaseButton/BaseButton";
import BaseTypography from "@ui/atoms/BaseTypography/BaseTypography";
import { useEffect } from "react";

import {
  ConfirmationActionsRow,
  ConfirmationCardSurface
} from "./AIAssistantChatShell.components";
import Icon from "@ui/atoms/Icon/Icon";

export type ConfirmationCardProps = {
  /** Short heading, e.g. "Delete employee". NOT rendered visibly — the card
   * always shows a fixed "Are you sure?" and the agent's transcript message
   * states the specific action. Used only as the accessible name fallback
   * (`aria-label`) when `body` is empty. */
  title: string;
  /** Grounded consequence — used as the accessible description (the agent's
   * own message already states it in the transcript, so it isn't shown again). */
  body: string;
  /** Run the pending action. Bound to the Confirm button and the Enter key. */
  onConfirm: () => void;
  /** Dismiss without acting. Bound to the Cancel button and the Esc key. */
  onCancel: () => void;
  /** While a confirm/cancel turn is in flight, ignore further key/clicks. */
  disabled?: boolean;
};

// A Confirm/Cancel prompt rendered inline when a (delete) write tool returns
// `pending_confirmation`. The agent's message supplies the context, so the card
// is just the decision: a short title and two atom buttons. Enter confirms, Esc
// cancels — caught on a capturing window listener so the keys work even while
// focus is in the composer.
export const ConfirmationCard = ({
  body,
  disabled = false,
  onCancel,
  onConfirm,
  title
}: ConfirmationCardProps): JSX.Element => {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (disabled) return;
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        e.stopImmediatePropagation();
        onConfirm();
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopImmediatePropagation();
        onCancel();
      }
    };
    // Capture phase so this beats the composer's bubble-phase Enter handler.
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [disabled, onCancel, onConfirm]);

  return (
    <ConfirmationCardSurface aria-label={body || title} role="group">
      {/* Fixed header — the agent's message already states the specific action;
          the grounded `body` stays as the aria-label for screen readers. */}
      <BaseTypography
        variant="body2"
        weight="semiBold"
        color="text.primary"
        text="Are you sure?"
      />

      <ConfirmationActionsRow>
        <BaseButton
          disabled={disabled}
          text="Cancel"
          variant="text"
          onClick={onCancel}
        />
        <BaseButton
          disabled={disabled}
          endIcon={<Icon name="enter" size="sm" />}
          text="Confirm"
          variant="contained"
          onClick={onConfirm}
        />
      </ConfirmationActionsRow>
    </ConfirmationCardSurface>
  );
};
